/**
 * ProficiencyWatch (v6.36.25) — Super-admin page showing the daily auto-scanned
 * feed of WIDA / NY State / NYC DOE / federal MLL news items, each
 * auto-scored for MOSAIC relevance with product-improvement suggestions.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, RefreshCw, ExternalLink, TrendingUp, TrendingDown,
  AlertTriangle, Sparkles, X, Loader2, Newspaper, Lightbulb, Clock,
} from 'lucide-react';
import { formatTimeAgo } from '../utils/timeAgo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPACT_BADGE = {
  strength: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300', icon: TrendingUp, label: 'Strength' },
  weakness: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', icon: TrendingDown, label: 'Weakness' },
  opportunity: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', icon: Sparkles, label: 'Opportunity' },
  neutral: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', icon: AlertTriangle, label: 'Neutral' },
};

const ProficiencyWatch = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [minRelevance, setMinRelevance] = useState(0);

  const fetchData = async (relevance = minRelevance) => {
    setLoading(true);
    try {
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/proficiency-watch/items`, { params: { limit: 100, min_relevance: relevance } }),
        axios.get(`${API}/proficiency-watch/stats`),
      ]);
      setItems(itemsRes.data?.items || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load WIDA Watch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/proficiency-watch/scan`);
      toast.success(
        `Scan complete: ${res.data?.scored_items || 0} new items (${res.data?.high_relevance_items || 0} high-relevance)`,
      );
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await axios.post(`${API}/proficiency-watch/dismiss`, { item_id: id });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success('Dismissed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dismiss failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="proficiency-watch-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Link to="/admin">
          <Button variant="ghost" size="sm" className="h-8 px-2">
            <ArrowLeft size={18} />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="proficiency-watch-title">
          WIDA Watch
        </h1>
      </div>
      <p className="text-sm text-gray-500 mb-6 ml-11">
        Daily auto-scanned news from WIDA, NYSED, NYC DOE, and federal MLL policy — auto-scored for MOSAIC relevance, strength/weakness analysis, and product-improvement suggestions.
      </p>

      {/* Stats + manual scan */}
      <div className="grid sm:grid-cols-4 gap-0 border-t border-l border-gray-300 mb-6">
        <div className="border-b border-r border-gray-300 p-4">
          <div className="text-xs text-gray-500 uppercase">Total items</div>
          <div className="text-2xl font-bold mt-1" data-testid="ww-total">{stats?.total_items ?? '—'}</div>
        </div>
        <div className="border-b border-r border-gray-300 p-4">
          <div className="text-xs text-gray-500 uppercase">High Relevance (≥40)</div>
          <div className="text-2xl font-bold mt-1 text-[#002FA7]" data-testid="ww-high">{stats?.high_relevance_items ?? '—'}</div>
        </div>
        <div className="border-b border-r border-gray-300 p-4">
          <div className="text-xs text-gray-500 uppercase">Last Scan</div>
          <div className="text-sm font-semibold mt-1" data-testid="ww-lastscan">
            {stats?.last_run?.completed_at
              ? new Date(stats.last_run.completed_at).toLocaleString()
              : 'Never'}
          </div>
        </div>
        <div className="border-b border-r border-gray-300 p-4 flex items-center justify-center">
          <Button
            onClick={handleScan}
            disabled={scanning}
            className="btn-sharp-primary w-full"
            data-testid="ww-scan-btn"
          >
            {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Run Scan Now</>}
          </Button>
        </div>
      </div>

      {/* Relevance filter */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm text-gray-500 mr-2">Min relevance:</span>
        {[0, 30, 50, 70].map((v) => (
          <Button
            key={v}
            variant={minRelevance === v ? 'default' : 'outline'}
            size="sm"
            className={`h-8 ${minRelevance === v ? 'bg-[#002FA7] text-white' : 'btn-sharp'}`}
            onClick={() => { setMinRelevance(v); fetchData(v); }}
            data-testid={`ww-filter-${v}`}
          >
            {v === 0 ? 'All' : `≥ ${v}`}
          </Button>
        ))}
        <span className="ml-auto text-sm text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Items */}
      {loading && (
        <div className="text-center py-16 text-gray-400">
          <Loader2 className="w-8 h-8 mx-auto animate-spin" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200" data-testid="ww-empty">
          <Newspaper className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <h3 className="font-semibold text-gray-500">No items yet</h3>
          <p className="text-sm text-gray-400 mt-1">Click "Run Scan Now" to fetch the latest news.</p>
        </div>
      )}

      <div className="space-y-4" data-testid="ww-list">
        {items.map((it) => {
          const impact = IMPACT_BADGE[it.mosaic_impact] || IMPACT_BADGE.neutral;
          const Icon = impact.icon;
          return (
            <div
              key={it.id}
              className={`border-2 ${impact.border} p-5`}
              data-testid={`ww-item-${it.id}`}
            >
              {/* Header row */}
              <div className="flex items-start gap-3 mb-3">
                <div className={`px-2 py-1 ${impact.bg} ${impact.text} text-xs font-bold uppercase flex items-center gap-1 flex-shrink-0`}>
                  <Icon size={12} /> {impact.label}
                </div>
                <div className="text-xs px-2 py-1 bg-[#002FA7] text-white font-bold flex-shrink-0">
                  {it.relevance_score}/100
                </div>
                <div className="text-xs text-gray-500 flex-shrink-0">{it.source}</div>
                <button
                  type="button"
                  onClick={() => handleDismiss(it.id)}
                  className="ml-auto text-gray-400 hover:text-gray-700"
                  data-testid={`ww-dismiss-${it.id}`}
                  aria-label="Dismiss this item"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Title */}
              <a
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-semibold text-gray-900 hover:text-[#002FA7] inline-flex items-start gap-1"
              >
                {it.title} <ExternalLink size={12} className="mt-1.5 flex-shrink-0" />
              </a>

              {/* Automated summary */}
              {it.summary && (
                <p className="text-sm text-gray-700 mt-2">{it.summary}</p>
              )}

              {/* Impact analysis */}
              {it.impact_note && (
                <div className={`mt-3 p-3 border-l-4 ${impact.border.replace('border-', 'border-l-')} ${impact.bg}`}>
                  <div className="text-xs font-bold uppercase text-gray-600 mb-1">Impact on MOSAIC</div>
                  <p className="text-sm text-gray-800">{it.impact_note}</p>
                </div>
              )}

              {/* Product suggestions */}
              {it.product_suggestions?.length > 0 && (
                <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500">
                  <div className="text-xs font-bold uppercase text-gray-600 mb-2 flex items-center gap-1">
                    <Lightbulb size={12} className="text-amber-600" /> Suggested Product Changes
                  </div>
                  <ul className="text-sm text-gray-800 space-y-1 list-disc list-inside">
                    {it.product_suggestions.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {it.tags?.length > 0 && (
                <div className="mt-3 flex gap-1 flex-wrap">
                  {it.tags.map((t) => (
                    <span key={t} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600">{t}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Symmetric daemon-observability footer (v6.36.31) — mirrors the
          one on ParityAudit so operators see at-a-glance whether the
          nightly RSS scan is firing without leaving the dashboard. */}
      {stats && (
        <div
          className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500 flex items-center gap-2 flex-wrap"
          data-testid="ww-lastscan-footer"
        >
          <Clock className="w-3.5 h-3.5" />
          {stats.last_run?.completed_at ? (
            <span data-testid="ww-lastscan-summary">
              <span className="font-bold">Last WIDA scan:</span>{' '}
              <span>{formatTimeAgo(stats.last_run.completed_at)}</span>
              {' · '}
              <span className="font-mono text-green-700">{stats.last_run.deduped_new_items ?? 0} new items</span>
              {' · '}
              <span className="font-mono">{stats.last_run.high_relevance_items ?? 0} high-relevance</span>
              {' · '}
              <span className="font-mono">{stats.total_items ?? 0} cached total</span>
              {' · '}
              <span className="font-mono">{stats.last_run.queries_attempted ?? 0} sources</span>
              {(stats.last_run.errors?.length || 0) > 0 && (
                <>
                  {' · '}
                  <span className="font-mono text-red-700">{stats.last_run.errors.length} errors</span>
                </>
              )}
            </span>
          ) : (
            <span>
              <span className="font-bold">Nightly RSS scan:</span> not run yet on this pod. Click <span className="font-bold">Run Scan Now</span> above to fire one manually, or wait — the daily loop will trigger within 24h.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProficiencyWatch;
